import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDateTime } from '../../utils/formatters.js';

/**
 * Kantonale Lohnstammdaten: Quellensteuertarife und Familienzulagen.
 *
 * Beide haben denselben Grund, hier zu stehen: sie sind kantonal, sie ändern
 * jährlich, und sie werden NICHT mitgeliefert. Ein erfundener Wert sieht in
 * beiden Fällen amtlich aus und ist für die betroffene Person dauerhaft
 * nachteilig - beim Steuersatz zu viel abgezogen, bei der Zulage zu wenig
 * ausbezahlt.
 *
 * Die Reihenfolge auf dieser Seite ist der Punkt: erst sehen, dann übernehmen.
 * Wer eine Tarifdatei einliest, kann nicht prüfen, ob die Zahlen stimmen -
 * aber er kann sehen, ob sie plausibel sind. Erfundene oder falsch gelesene
 * Sätze ziehen jedem Betroffenen monatlich den falschen Betrag ab, jahrelang,
 * bis eine Nachforderung kommt.
 *
 * Die Satzspanne steht deshalb gross da. Eine falsche Skalierung erkennt nur
 * ein Mensch: aus 8.35 % werden 0.0835 %, und das sieht aus wie ein niedriger
 * Tarif statt wie ein Lesefehler.
 */
/** Die 26 Kantone. */
const KANTONE = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE',
  'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH',
];

export function WithholdingTaxPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [format, setFormat] = useState('csv');
  const [quelle, setQuelle] = useState('');
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [erfolg, setErfolg] = useState(null);

  const [zulage, setZulage] = useState({
    canton: '', validYear: String(new Date().getFullYear()), childAmount: '', educationAmount: '', source: '',
  });
  const [zulagenFehler, setZulagenFehler] = useState(null);

  const zulagen = useQuery({
    queryKey: ['hr', 'family-allowances'],
    queryFn: () => hrApi.listFamilyAllowances().then((r) => r.items || []),
  });

  const zulageSpeichern = useMutation({
    mutationFn: () => hrApi.setFamilyAllowance({
      canton: zulage.canton,
      validYear: Number(zulage.validYear),
      childAmount: Number(zulage.childAmount),
      educationAmount: Number(zulage.educationAmount),
      source: zulage.source || null,
    }),
    onSuccess: () => {
      setZulagenFehler(null);
      setZulage((z) => ({ ...z, childAmount: '', educationAmount: '' }));
      queryClient.invalidateQueries({ queryKey: ['hr', 'family-allowances'] });
    },
    onError: (e) => setZulagenFehler(e.message),
  });

  const tarife = useQuery({
    queryKey: ['hr', 'withholding-tax', 'tariffs'],
    queryFn: () => hrApi.listWithholdingTariffs().then((r) => r.items || []),
  });

  const lesen = useMutation({
    mutationFn: () => hrApi.previewWithholdingTariffs({ text, format }),
    onSuccess: (d) => { setVorschau(d); setFehler(null); setErfolg(null); },
    onError: (e) => { setVorschau(null); setFehler(e.message); },
  });

  const uebernehmen = useMutation({
    mutationFn: (ignoriereBefunde) => hrApi.importWithholdingTariffs({
      text, format, source: quelle || null, ignoriereBefunde,
    }),
    onSuccess: (d) => {
      setErfolg(t('hr.qst.imported', { tarife: d.tarife, stufen: d.stufen }));
      setFehler(null);
      setVorschau(null);
      setText('');
      queryClient.invalidateQueries({ queryKey: ['hr', 'withholding-tax'] });
    },
    onError: (e) => { setFehler(e.message); setErfolg(null); },
  });

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.qst.title')}</h1>
          <p className="muted">{t('hr.qst.subtitle')}</p>
        </div>
      </header>

      <Card title={t('hr.qst.current')}>
        <Table
          isLoading={tarife.isLoading}
          rows={tarife.data || []}
          getRowKey={(r) => `${r.canton}-${r.tariffCode}-${r.validYear}`}
          emptyMessage={t('hr.qst.empty')}
          columns={[
            { key: 'canton', label: t('hr.qst.col.canton'), render: (r) => <span className="mono">{r.canton}</span> },
            { key: 'tariffCode', label: t('hr.qst.col.code'), render: (r) => <span className="mono">{r.tariffCode}</span> },
            { key: 'validYear', label: t('hr.qst.col.year'), align: 'right' },
            { key: 'stufen', label: t('hr.qst.col.brackets'), align: 'right' },
            {
              key: 'rate',
              label: t('hr.qst.col.rateRange'),
              align: 'right',
              render: (r) => <span className="mono num">{`${r.rateFrom} - ${r.rateTo} %`}</span>,
            },
            { key: 'source', label: t('hr.qst.col.source'), render: (r) => r.source || '-' },
            { key: 'importedAt', label: t('hr.qst.col.imported'), render: (r) => formatDateTime(r.importedAt) },
          ]}
        />
      </Card>

      <Card title={t('hr.qst.import')}>
        {/* Der Satz, der erklärt, warum diese Tabelle leer ausgeliefert wird. */}
        <p className="muted">{t('hr.qst.importIntro')}</p>

        <div className="toolbar">
          <label className="field" style={{ maxWidth: 220 }}>
            <span className="field-label">{t('hr.qst.format')}</span>
            <select className="input" value={format} onChange={(e) => { setFormat(e.target.value); setVorschau(null); }}>
              <option value="csv">{t('hr.qst.format.csv')}</option>
              <option value="estv">{t('hr.qst.format.estv')}</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('hr.qst.source')}</span>
            <input className="input" value={quelle} onChange={(e) => setQuelle(e.target.value)} placeholder={t('hr.qst.sourceHint')} maxLength={120} />
          </label>
        </div>

        {format === 'csv' && (
          <p className="muted text-sm mono">{t('hr.qst.csvHeader')}</p>
        )}

        <label className="field">
          <span className="field-label">{t('hr.qst.fileContent')}</span>
          <textarea
            className="input"
            rows={8}
            value={text}
            onChange={(e) => { setText(e.target.value); setVorschau(null); }}
            placeholder={t('hr.qst.filePlaceholder')}
          />
        </label>

        <div className="toolbar">
          <Button disabled={!text.trim() || lesen.isPending} onClick={() => lesen.mutate()}>
            {t('hr.qst.action.preview')}
          </Button>
          {/* Übernehmen erst, wenn gelesen wurde. Ein Knopf, der beides in
              einem tut, macht die Vorschau zur Zierde. */}
          <Button
            variant="primary"
            disabled={!vorschau || uebernehmen.isPending}
            onClick={() => uebernehmen.mutate(false)}
          >
            {t('hr.qst.action.import')}
          </Button>
        </div>

        {fehler && <div className="alert danger">{fehler}</div>}
        {erfolg && <div className="alert success">{erfolg}</div>}

        {vorschau && (
          <div className="qst-preview">
            <div className="card-grid">
              <div className="metric-card">
                <span className="metric-label">{t('hr.qst.preview.tariffs')}</span>
                <span className="metric-value">{vorschau.tarife.length}</span>
              </div>
              <div className="metric-card">
                <span className="metric-label">{t('hr.qst.preview.brackets')}</span>
                <span className="metric-value">{vorschau.stufen}</span>
              </div>
              {/* Die wichtigste Zahl auf dieser Seite. Eine falsche
                  Skalierung erkennt nur ein Mensch. */}
              <div className="metric-card">
                <span className="metric-label">{t('hr.qst.preview.rateRange')}</span>
                <span className="metric-value">{`${vorschau.satzVon} - ${vorschau.satzBis} %`}</span>
              </div>
            </div>

            <p className={vorschau.befunde.length ? 'alert warning' : 'muted'}>{vorschau.hinweis}</p>

            {vorschau.befunde.length > 0 && (
              <>
                <ul className="qst-findings">
                  {vorschau.befunde.slice(0, 10).map((b) => <li key={b}>{b}</li>)}
                </ul>
                {/* Befunde zu übergehen ist erlaubt, aber nur ausdrücklich -
                    und der Knopf sagt, was man dabei in Kauf nimmt. */}
                <Button variant="ghost" disabled={uebernehmen.isPending} onClick={() => uebernehmen.mutate(true)}>
                  {t('hr.qst.action.importAnyway')}
                </Button>
              </>
            )}

            <h3 className="report-section">{t('hr.qst.preview.samples')}</h3>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('hr.qst.col.canton')}</th>
                    <th>{t('hr.qst.col.code')}</th>
                    <th>{t('hr.qst.col.year')}</th>
                    <th className="num">{t('hr.qst.col.from')}</th>
                    <th className="num">{t('hr.qst.col.to')}</th>
                    <th className="num">{t('hr.qst.col.rate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {vorschau.beispiele.map((b, i) => (
                    <tr key={`${b.canton}-${b.tariffCode}-${b.incomeFromCents}-${i}`}>
                      <td className="mono">{b.canton}</td>
                      <td className="mono">{b.tariffCode}</td>
                      <td>{b.validYear}</td>
                      <td className="num">{(b.incomeFromCents / 100).toFixed(2)}</td>
                      <td className="num">{b.incomeToCents === null ? '-' : (b.incomeToCents / 100).toFixed(2)}</td>
                      <td className="num">{`${b.ratePercent} %`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
          <Card title={t('hr.fam.title')}>
        {/* Warum auch diese Tabelle leer ausgeliefert wird. */}
        <p className="muted">{t('hr.fam.intro')}</p>

        <Table
          isLoading={zulagen.isLoading}
          rows={zulagen.data || []}
          getRowKey={(r) => `${r.canton}-${r.validYear}`}
          emptyMessage={t('hr.fam.empty')}
          columns={[
            { key: 'canton', label: t('hr.qst.col.canton'), render: (r) => <span className="mono">{r.canton}</span> },
            { key: 'validYear', label: t('hr.qst.col.year'), align: 'right' },
            { key: 'child', label: t('hr.fam.col.child'), align: 'right', render: (r) => <span className="mono num">{(r.childCents / 100).toFixed(2)}</span> },
            { key: 'education', label: t('hr.fam.col.education'), align: 'right', render: (r) => <span className="mono num">{(r.educationCents / 100).toFixed(2)}</span> },
            { key: 'source', label: t('hr.qst.col.source'), render: (r) => r.source || '-' },
          ]}
        />

        <div className="toolbar">
          <label className="field" style={{ maxWidth: 140 }}>
            <span className="field-label">{t('hr.qst.col.canton')}</span>
            <select className="input" value={zulage.canton} onChange={(e) => setZulage((z) => ({ ...z, canton: e.target.value }))}>
              <option value="">-</option>
              {KANTONE.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 120 }}>
            <span className="field-label">{t('hr.qst.col.year')}</span>
            <input className="input num" type="number" min="2000" max="2100" value={zulage.validYear} onChange={(e) => setZulage((z) => ({ ...z, validYear: e.target.value }))} />
          </label>
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('hr.fam.col.child')}</span>
            <input className="input num" type="number" min="0" step="0.05" value={zulage.childAmount} onChange={(e) => setZulage((z) => ({ ...z, childAmount: e.target.value }))} />
          </label>
          <label className="field" style={{ maxWidth: 180 }}>
            <span className="field-label">{t('hr.fam.col.education')}</span>
            <input className="input num" type="number" min="0" step="0.05" value={zulage.educationAmount} onChange={(e) => setZulage((z) => ({ ...z, educationAmount: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field-label">{t('hr.qst.col.source')}</span>
            <input className="input" value={zulage.source} onChange={(e) => setZulage((z) => ({ ...z, source: e.target.value }))} maxLength={120} />
          </label>
          <Button
            variant="primary"
            disabled={!zulage.canton || zulage.childAmount === '' || zulage.educationAmount === '' || zulageSpeichern.isPending}
            onClick={() => zulageSpeichern.mutate()}
          >
            {t('hr.fam.action.save')}
          </Button>
        </div>

        {/* Die Ausbildungszulage wird erfasst, aber noch nicht angewendet -
            das muss dastehen, sonst rechnet jemand damit. */}
        <p className="muted text-sm">{t('hr.fam.educationNote')}</p>
        {zulagenFehler && <div className="alert danger">{zulagenFehler}</div>}
      </Card>

</section>
  );
}
